import { useEffect, useRef, useState } from "react";
import { Image } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { UploadService } from "@/services/APIs";
import CustomButton from "../common/Button";
import { Trash } from "lucide-react";

type ImageUploadValue =
  | string
  | { id: string; url: string; name: string; size: number }
  | string[]
  | { id: string; url: string; name: string; size: number }[]
  | null
  | undefined;

type CustomImageUploadProps = {
  folderName?: string;
  onUploaded?: (response: any, file: File) => void;
  width?: number; // preferred simple width alias (px)
  boxWidth?: number;
  boxHeight?: number;
  disabled?: boolean;
  accept?: string;
  shape?: "square" | "circle";
  borderStyle?: "solid" | "dashed" | "dotted" | "double" | "hidden";
  multiple?: boolean;
  value?: ImageUploadValue;
  onChange?: (value: ImageUploadValue) => void;
  initialUrl?: string;
  className?: string;
};

const tryParseJsonArray = (raw: string): unknown[] | null => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const isStringifiedArrayValue = (raw: unknown): boolean =>
  typeof raw === "string" && tryParseJsonArray(raw) !== null;

export const CustomImageUpload: React.FC<CustomImageUploadProps> = ({
  folderName = "images",
  onUploaded,
  width,
  boxWidth,
  boxHeight,
  disabled = false,
  accept = "image/*",
  borderStyle = "dashed",
  className,
  value,
  onChange,
  initialUrl,
  multiple = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const DEFAULT_SIZE = 160;
  const W = width ?? boxWidth ?? DEFAULT_SIZE;
  const H = boxHeight ?? DEFAULT_SIZE;

  // states
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialUrl ?? null
  );
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [isObjectPreview, setIsObjectPreview] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHovered, setIsHovered] = useState<number | null>(null);
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);

  // Load existing files
  useEffect(() => {
    let isActive = true;
    const fetchFiles = async () => {
      if (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        value === "[]" ||
        value === ""
      ) {
        if (multiple) {
          setPreviewUrls([]);
          setUploadedFileIds([]);
        } else {
          setPreviewUrl(null);
        }
        return;
      }

      try {
        let parsedValue: ImageUploadValue = value;
        if (typeof value === "string") {
          const parsed = tryParseJsonArray(value);
          if (parsed) {
            parsedValue = parsed as string[];
          }
        }

        if (multiple && Array.isArray(parsedValue)) {
          const urls: string[] = [];
          const ids: string[] = [];
          for (const val of parsedValue) {
            const url = typeof val === "string" ? val : val?.url;
            const id = typeof val === "string" ? val : val?.id;
            if (!url) {
              continue;
            }
            if (id) {
              ids.push(id);
            }
            const response = await UploadService.getFileById({
              id: url as string,
            });
            urls.push(response?.file_path_thumbnail ?? response?.file_path);
          }
          if (isActive) {
            setPreviewUrls(urls);
            setUploadedFileIds(ids);
          }
        } else if (!multiple) {
          let url: string;
          if (typeof value === "string") {
            url = value;
          } else if (value && typeof value === "object" && "url" in value) {
            url = value.url;
          } else {
            return;
          }
          const response = await UploadService.getFileById({
            id: url as string,
          });
          if (isActive) {
            setPreviewUrl(response?.file_path_thumbnail ?? response?.file_path);
            setFullImageUrl(response?.file_path);
          }
        }
      } catch (error) {
        console.error("Error fetching file details:", error);
      }
    };
    fetchFiles();
    return () => {
      isActive = false;
    };
  }, [value, multiple]);

  // cleanup
  useEffect(() => {
    return () => {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isObjectPreview, previewUrl]);

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const extractUrl = (res: any): string | null =>
    res?.[0]?.file_path ??
    res?.file_path ??
    res?.data?.[0]?.url ??
    res?.files?.[0]?.url ??
    res?.url ??
    null;

  const extractId = (res: any): string | null =>
    res?.[0]?.id ??
    res?.id ??
    res?.data?.[0]?.id ??
    res?.files?.[0]?.id ??
    null;

  // ✅ Handle Upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) {
      return;
    }

    if (multiple) {
      const validFiles = Array.from(files).filter(file =>
        file.type.startsWith("image/")
      );

      if (validFiles.length === 0) {
        return;
      }

      try {
        const response = await UploadService.uploadFiles({
          folderName,
          files: validFiles,
        });

        const responseArray = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.results)
              ? response.results
              : [response];

        const newUrls: string[] = [];
        const newIds: string[] = [];

        for (const resItem of responseArray) {
          const finalUrl =
            resItem?.file_path ?? resItem?.url ?? extractUrl(resItem);
          const fileId = resItem?.id ?? extractId(resItem);

          if (finalUrl) {
            newUrls.push(finalUrl);
          }
          if (fileId) {
            newIds.push(fileId);
          }
        }

        const finalIds = [...uploadedFileIds, ...newIds];
        setUploadedFileIds(finalIds);
        setPreviewUrls([...previewUrls, ...newUrls]);

        const finalValue = isStringifiedArrayValue(value)
          ? JSON.stringify(finalIds)
          : finalIds;
        onChange?.(finalValue as any);
        onUploaded?.(response, validFiles[0]);
      } catch (error) {
        console.error("Upload failed:", error);
      }
    } else {
      // SINGLE UPLOAD
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        e.target.value = "";
        return;
      }

      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const tempUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(tempUrl);
      setIsObjectPreview(true);

      try {
        const response = await UploadService.uploadFiles({
          folderName,
          files: [file],
        });

        const finalUrl = extractUrl(response);
        const fileId = extractId(response);

        if (finalUrl) {
          if (isObjectPreview && previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewUrl(finalUrl);
          setIsObjectPreview(false);
          onChange?.(fileId ?? null);
          onUploaded?.(response, file);
        }
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }
    e.target.value = "";
  };

  const clearSelection = (index?: number) => {
    if (multiple && index !== undefined) {
      const newUrls = previewUrls.filter((_, i) => i !== index);
      const newIds = uploadedFileIds.filter((_, i) => i !== index);
      setPreviewUrls(newUrls);
      setUploadedFileIds(newIds);

      const finalValue = isStringifiedArrayValue(value)
        ? JSON.stringify(newIds)
        : newIds;
      onChange?.(finalValue as any);
    } else {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsObjectPreview(false);
      onChange?.(null);
    }
  };

  // border styles
  const borderStyleClass =
    borderStyle === "dashed"
      ? "border-dashed"
      : borderStyle === "dotted"
        ? "border-dotted"
        : borderStyle === "double"
          ? "border-double"
          : borderStyle === "hidden"
            ? "border-none"
            : "";

  const borderWidthClass = borderStyle === "hidden" ? "border-0" : "border-2";
  const borderColorClass =
    borderStyle === "hidden" ? "" : "border-gray-300 hover:border-gray-400";

  // ✅ Conditional render
  return (
    <div className={`space-y-2 ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {multiple ? (
        <div className="flex flex-wrap gap-3">
          {previewUrls.map((url, index) => (
            <div
              key={url}
              className="relative"
              style={{ width: W, height: H }}
              onMouseEnter={() => setIsHovered(index)}
              onMouseLeave={() => setIsHovered(null)}
            >
              <Image
                src={url}
                alt={`img-${index}`}
                width={W}
                height={H}
                style={{
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px dashed #d9d9d9",
                }}
              />
              {isHovered === index && (
                <CustomButton
                  danger
                  icon={<Trash size={16} />}
                  type="text"
                  onClick={() => clearSelection(index)}
                  className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
                />
              )}
            </div>
          ))}

          {!disabled && (
            <div
              onClick={openPicker}
              className={[
                "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
                borderWidthClass,
                borderStyleClass,
                borderColorClass,
              ].join(" ")}
              style={{ width: W, height: H }}
            >
              <PlusOutlined style={{ fontSize: 28 }} />
            </div>
          )}
        </div>
      ) : !previewUrl ? (
        <div
          role="button"
          tabIndex={0}
          onClick={openPicker}
          className={[
            "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
            borderWidthClass,
            borderStyleClass,
            borderColorClass,
          ].join(" ")}
          style={{ width: W, height: H }}
        >
          <PlusOutlined style={{ fontSize: 28 }} />
        </div>
      ) : (
        <div
          className="relative cursor-pointer"
          style={{ width: W, height: H }}
          onMouseEnter={() => setIsHovered(0)}
          onMouseLeave={() => setIsHovered(null)}
        >
          <Image
            src={previewUrl}
            alt={selectedFile?.name ?? "image"}
            width={W}
            height={H}
            style={{
              objectFit: "cover",
              borderRadius: 8,
              border: "1px dashed #d9d9d9",
            }}
            preview={{
              mask: <span style={{ fontSize: 16 }}>Preview</span>,
              src: fullImageUrl ?? previewUrl,
            }}
          />
          {isHovered === 0 && (
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              onClick={() => clearSelection()}
              className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
            />
          )}
        </div>
      )}
    </div>
  );
};
